import React from 'react';
import { CheckCircleIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Box, Button, Alert, AlertDescription, Text, Flex
} from '@chakra-ui/react';

const SubscriptionSection = ({ boUserData, t }) => {
  
  // Check if subscription section should be shown
  const shouldShowSubscription = () => {
    return boUserData && boUserData.subscription_type !== false;
  };

  // Get subscription status logic
  const getSubscriptionStatus = () => {
    // If subscription_type is falsy (null, undefined, empty string) or 'none'
    if (!boUserData?.subscription_type || boUserData.subscription_type === 'none') {
      // If there's a cancellation date, check if it's in the future or past
      if (boUserData.cancellation_date) {
        const expirationDate = new Date(boUserData.cancellation_date);
        const now = new Date();
        // If cancellation date is in the future, this is an error state
        if (expirationDate > now) {
          return 'error';
        }
        // If cancellation date is in the past, subscription expired
        return 'expired';
      }
      // No cancellation date means never had a subscription
      return 'none';
    }
    
    // Has a subscription type (digital/hardcopy), check if still active
    if (boUserData.cancellation_date) {
      const expirationDate = new Date(boUserData.cancellation_date);
      const now = new Date();
      return expirationDate > now ? 'active' : 'expired';
    }
    
    // Has subscription type but no cancellation date - active
    return 'active';
  };

  // Format subscription type for display
  const formatSubscriptionType = (type) => {
    switch(type) {
      case 'digital':
        return t('myAccount.subscription.digitalOnly');
      case 'hardcopy':
        return t('myAccount.subscription.digitalAndPrint');
      default:
        return t('myAccount.subscription.none');
    }
  };

  // Get subscription status text
  const getSubscriptionStatusText = () => {
    const status = getSubscriptionStatus();
    const subscriptionType = boUserData.subscription_type;
    const expirationDate = boUserData.cancellation_date 
      ? new Date(boUserData.cancellation_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : null;

    switch(status) {
      case 'none':
        return t('myAccount.subscription.statusNoSubscription');
      
      case 'error':
        return t('myAccount.subscription.statusError', { date: expirationDate });
      
      case 'expired':
        return t('myAccount.subscription.statusExpired', { date: expirationDate });
      
      case 'active':
        const typeText = subscriptionType === 'digital' 
          ? t('myAccount.subscription.digitalOnly').toLowerCase()
          : t('myAccount.subscription.digitalAndPrint').toLowerCase();

        return expirationDate 
          ? t('myAccount.subscription.statusActive', { type: typeText, date: expirationDate })
          : t('myAccount.subscription.statusActiveNoDate', { type: typeText });
      
      default:
        return t('myAccount.subscription.statusNoSubscription');
    }
  };

  // Get subscription alert
  const getSubscriptionAlert = () => {
    const status = getSubscriptionStatus();
    
    switch(status) {
      case 'none':
        return (
          <Alert status="info" borderRadius="md" width="fit-content">
            <InfoIcon />
            <Box ml={3}>
              <AlertDescription>
                {t('myAccount.subscription.noSubscriptionMessage')}{' '}
                <Button 
                  as="a" 
                  href="/subscription-purchase" 
                  colorScheme="blue" 
                  size="sm" 
                  ml={2}
                >
                  {t('myAccount.subscription.purchaseSubscription')}
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        );
      
      case 'error':
        return (
          <Alert status="error" borderRadius="md" width="fit-content">
            <WarningIcon />
            <Box ml={3}>
              <AlertDescription>
                {t('myAccount.subscription.errorMessage')}{' '}
                <Button 
                  as="a" 
                  href="/contact" 
                  colorScheme="red" 
                  size="sm" 
                  ml={2}
                >
                  {t('myAccount.subscription.contactOffice')}
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        );
      
      case 'expired':
        return (
          <Alert status="warning" borderRadius="md" width="fit-content">
            <WarningIcon />
            <Box ml={3}>
              <AlertDescription>
                {t('myAccount.subscription.expiredMessage')}{' '}
                <Button 
                  as="a" 
                  href="/subscription-purchase" 
                  colorScheme="orange" 
                  size="sm" 
                  ml={2}
                >
                  {t('myAccount.subscription.renewSubscription')}
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        );
      
      case 'active':
        return (
          <Alert status="success" borderRadius="md" width="fit-content">
            <CheckCircleIcon />
            <Box ml={3}>
              <AlertDescription>
                {t('myAccount.subscription.activeMessage')}{' '}
                <Button 
                  as="a" 
                  href="/subscription-magazine" 
                  colorScheme="green" 
                  size="sm" 
                  ml={2}
                >
                  {t('myAccount.subscription.viewMagazine')}
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        );
      
      default:
        return null;
    }
  };

  // Don't render if subscription should not be shown
  if (!shouldShowSubscription()) {
    return null;
  }

  return (
    <Box 
      borderWidth="1px" 
      borderRadius="lg" 
      p={6} 
      mb={6} 
      bg="gray.50" 
      borderColor="gray.200"
    >
      <Flex alignItems="center" mb={4}>                  
        <Box fontSize="xl" fontWeight="bold" color="gray.800" mr={3}>
          {t('myAccount.subscription.title')}
        </Box>
      </Flex>

      {boUserData && (
        <>
          {/* Human Readable Subscription Status */}
          <Text fontSize="lg" fontWeight="medium" mb={4}>
            {getSubscriptionStatusText()}
          </Text>

          {/* Subscription Status Alert with Action */}
          {getSubscriptionAlert()}
        </>
      )}
    </Box>
  );
};

export default SubscriptionSection;